import React, { useState, useMemo, useRef, useEffect, useCallback, startTransition } from 'react';
import {
  Paper,
  IconButton,
  Box,
  Typography,
  Badge,
  OutlinedInput,
  InputAdornment,
  Popover,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Chip,
  Snackbar,
  Tooltip
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import SettingsIcon from '@mui/icons-material/Settings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ClearIcon from '@mui/icons-material/Clear';
import MapIcon from '@mui/icons-material/Map';
import PersonPinCircleIcon from '@mui/icons-material/PersonPinCircle';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import RouteIcon from '@mui/icons-material/Route';
import { List, useListRef } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';
import SidebarDeviceRow from './SidebarDeviceRow';

dayjs.extend(relativeTime);
dayjs.locale('es');

const KEYWORD_DEBOUNCE_MS = 200;
const STATUS_REFRESH_MS = 60_000;
const ROW_HEIGHT = 80;
const OVERSCAN_COUNT = 5;

const STATUS_COLOR_MAP = new Map([
  ['online', '#4caf50'],
  ['offline', '#f44336'],
  ['unknown', '#ff9800'],
]);

const ACTION_FILTERS = [
  { key: 'ON_WAY', icon: DirectionsCarIcon, label: 'En camino' },
  { key: 'PICKUP', icon: PersonPinCircleIcon, label: 'Recogiendo' },
  { key: 'START', icon: PlayCircleIcon, label: 'Iniciado' },
  { key: 'ACTIVE', icon: RouteIcon, label: 'En trayecto' }
];

const PAPER_SX = {
  width: '320px',
  minWidth: '320px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  bgcolor: 'background.paper',
  borderRadius: '8px',
  overflow: 'hidden',
};

const HEADER_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  py: 1,
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const FILTER_BUTTONS_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  px: 1.5,
  py: 1,
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: '#fafafa',
  overflowX: 'auto',
  '&::-webkit-scrollbar': {
    height: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: '#BDBDBD',
    borderRadius: 4,
  },
};

const ACTION_FILTER_BTN_SX = {
  minWidth: 'unset',
  width: 32,
  height: 32,
  borderRadius: '8px',
  padding: 0,
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.05)',
  },
};

const ACTION_FILTER_ACTIVE_SX = {
  backgroundColor: '#FF5400',
  color: 'white',
  '&:hover': {
    backgroundColor: '#E04A00',
  },
};

const ACTION_FILTER_INACTIVE_SX = {
  backgroundColor: 'transparent',
  color: '#BDBDBD',
  border: '1px solid #E0E0E0',
  '&:hover': {
    backgroundColor: '#F5F5F5',
    color: '#757575',
  },
};

const CHIPS_BAR_SX = {
  px: 2,
  py: 1,
  display: 'flex',
  gap: 0.5,
  flexWrap: 'wrap',
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: '#f5f5f5',
};

const CHIP_SX = { fontSize: '0.75rem' };
const CLEAR_CHIP_SX = { fontSize: '0.75rem', ml: 'auto' };

const LIST_AREA_SX = {
  flex: 1,
  minHeight: 0,
  overflow: 'hidden',
  bgcolor: '#fafafa',
};

const EMPTY_BOX_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '200px',
  px: 3,
  textAlign: 'center',
};

const LOADING_BOX_SX = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '200px',
};

const FOOTER_SX = {
  borderTop: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
  p: 1,
  display: 'flex',
  gap: 0.5,
};

const FOOTER_BTN_ACTIVE_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: '#1976d2',
  textTransform: 'none',
  fontSize: '0.688rem',
  fontWeight: 500,
  py: 0.75,
  minHeight: 'unset',
  '&:hover': { bgcolor: '#f5f5f5' },
};

const FOOTER_BTN_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  color: '#616161',
  textTransform: 'none',
  fontSize: '0.688rem',
  fontWeight: 400,
  py: 0.75,
  minHeight: 'unset',
  '&:hover': { bgcolor: '#f5f5f5' },
};

const SEARCH_INPUT_SX = {
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E0E0E0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#BDBDBD' },
  '& .MuiOutlinedInput-input': { py: 0.75, fontSize: '0.875rem' },
};

const MAP_ICON_BTN_SX = { color: '#616161' };
const MAP_ICON_SX = { fontSize: 20, mb: 0.25 };
const ASSESS_ICON_SX = { fontSize: 20, mb: 0.25 };
const SETTINGS_ICON_SX = { fontSize: 20, mb: 0.25 };
const EMPTY_CAR_ICON_SX = { fontSize: 48, color: '#BDBDBD', mb: 1 };
const POPOVER_PAPER_SX = { sx: { width: '280px' } };
const POPOVER_BOX_SX = { display: 'flex', flexDirection: 'column', p: 2, gap: 2 };

const formatStatus = (status) => {
  switch (status) {
    case 'online':
      return 'En línea';
    case 'offline':
      return 'Fuera de línea';
    case 'unknown':
      return 'hace …';
    default:
      return 'Desconocido';
  }
};

function getDeviceGroupIds(device, groups) {
  const ids = [];
  let gId = device.groupId;
  while (gId) {
    ids.push(gId);
    gId = groups[gId]?.groupId || 0;
  }
  return ids;
}

const TraccarSidebar = React.memo(
  ({
    devices = [],
    positions = [],
    groups = {},
    selectedDeviceId,
    onDeviceSelect,
    loading = false,
    onSettingsClick,
    onReportsClick,
  }) => {

    const [keyword, setKeyword] = useState('');
    const [debouncedKeyword, setDebouncedKeyword] = useState('');
    const [filter, setFilter] = useState({ statuses: ['online'], groups: [], actions: [] });
    const [filterSort, setFilterSort] = useState('');
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [filterEverOpened, setFilterEverOpened] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [tick, setTick] = useState(0);

    const inputRef = useRef();
    const listRef = useListRef();

    useEffect(() => {
      const id = setInterval(() => setTick((t) => t + 1), STATUS_REFRESH_MS);
      return () => clearInterval(id);
    }, []);

    useEffect(() => {
      const id = setTimeout(() => {
        startTransition(() => setDebouncedKeyword(keyword));
      }, KEYWORD_DEBOUNCE_MS);

      return () => clearTimeout(id);
    }, [keyword]);

    const statusCounts = useMemo(() => {
      const counts = { online: 0, offline: 0, unknown: 0 };
      for (const d of devices) {
        if (d.status in counts) counts[d.status]++;
        else counts.unknown++;
      }
      return counts;
    }, [devices]);

    const sortedGroups = useMemo(
      () =>
        Object.values(groups).sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        ),
      [groups]
    );

    const deviceInfoById = useMemo(() => {
      const m = new Map();
      for (const p of positions) {
        const batteryLevel = p.attributes?.batteryLevel?.parsedValue || p.attributes?.batteryLevel;
        let serviceInfo = null;
        let vehicleInfo = null;
        let partnerInfo = null;
        let action = null;
        let versionApp = null;
        let accuracy = null;
    
        try {
          const si = p.attributes?.service_info;
          if (si) serviceInfo = typeof si === 'string' ? JSON.parse(si) : si;
        } catch {}
    
        try {
          const vi = p.attributes?.vehicle_info;
          if (vi) vehicleInfo = typeof vi === 'string' ? JSON.parse(vi) : vi;
        } catch {}
    
        try {
          const pi = p.attributes?.partner_info;
          if (pi) partnerInfo = typeof pi === 'string' ? JSON.parse(pi) : pi;
        } catch {}
    
        action = p.attributes?.action || null;
        versionApp = p.attributes?.versionApp || null;
        accuracy = p.accuracy !== undefined ? p.accuracy : null;
    
        const hasService = !!(serviceInfo?.service_code && vehicleInfo?.plate);
        const partnerName = partnerInfo?.name || 'Sin partner';
        const plateNumber = vehicleInfo?.plate || 'Sin placas';
    
        m.set(p.deviceId, {
          position: p,
          batteryLevel,
          serviceInfo,
          vehicleInfo,
          partnerInfo,
          action,
          versionApp,
          accuracy,
          hasService,
          serviceDisplay: hasService
            ? `${partnerName} | Placas: ${plateNumber}`
            : 'Sin servicio asignado',
        });
      }
      return m;
    }, [positions]);

    const hasActiveFilters = filter.statuses.length > 0 || filter.groups.length > 0 || filter.actions.length > 0;

    const filteredDevices = useMemo(() => {
      const filtered = devices
        .filter(
          (d) =>
            !filter.statuses.length || filter.statuses.includes(d.status)
        )
        .filter(
          (d) =>
            !filter.groups.length ||
            getDeviceGroupIds(d, groups).some((id) =>
              filter.groups.includes(id)
            )
        )
        .filter((d) => {
          if (!filter.actions.length) return true;
          const deviceAction = deviceInfoById.get(d.id)?.action;
          return filter.actions.includes(deviceAction);
        })
        .filter((d) => {
          if (!debouncedKeyword) return true;
          const kw = debouncedKeyword.toLowerCase();

          return [d.name, d.uniqueId, d.phone, d.model, d.contact].some(
            (s) => s && s.toLowerCase().includes(kw)
          );
        });

      const statusOrder = { online: 0, offline: 1, unknown: 2 };
      filtered.sort((a, b) => {
        const statusDiff =
          (statusOrder[a.status] ?? 2) - (statusOrder[b.status] ?? 2);

        if (statusDiff !== 0) return statusDiff;
        switch (filterSort) {
          case 'name':
            return (a.name || '').localeCompare(b.name || '');
          case 'lastUpdate': {
            const t1 = a.lastUpdate ? dayjs(a.lastUpdate).valueOf() : 0;
            const t2 = b.lastUpdate ? dayjs(b.lastUpdate).valueOf() : 0;
            return t2 - t1;
          }
          default:
            return 0;
        }
      });

      return filtered;
    }, [devices, debouncedKeyword, filter, filterSort, groups, deviceInfoById]);

    const statusLabels = useMemo(() => {
      const m = new Map();
      for (const d of filteredDevices) {
        m.set(
          d.id,
          d.status === 'online'
            ? 'En línea'
            : d.lastUpdate
            ? dayjs(d.lastUpdate).fromNow()
            : 'Fuera de línea'
        );
      }
      return m;
    }, [filteredDevices, tick]);

    useEffect(() => {
      if (selectedDeviceId && listRef.current) {
        const idx = filteredDevices.findIndex(
          (d) => d.id === selectedDeviceId
        );

        if (idx !== -1) {
          requestAnimationFrame(() =>
            listRef.current?.scrollToRow({ index: idx, align: 'smart' })
          );
        }
      }
    }, [selectedDeviceId, filteredDevices]);

    const copyLinkRef = useRef(null);

    function handleCopyLinkStable(deviceId, e) {
      copyLinkRef.current?.(deviceId, e);
    }

    copyLinkRef.current = useCallback((deviceId, e) => {
      e.stopPropagation();
      const url = `${window.location.origin}${window.location.pathname}/device/${deviceId}`;

      navigator.clipboard
        .writeText(url)
        .then(() => setSnackbarOpen(true))
        .catch((err) => console.error('Error copiando link:', err));
    }, []);

    const handleActionFilterToggle = useCallback((actionKey) => {
      startTransition(() => {
        setFilter((prev) => {
          const currentActions = prev.actions;
          const newActions = currentActions.includes(actionKey)
            ? currentActions.filter(a => a !== actionKey)
            : [...currentActions, actionKey];
          
          return { ...prev, actions: newActions };
        });
      });
    }, []);

    const handleClearActionFilters = useCallback(() => {
      startTransition(() => {
        setFilter((prev) => ({ ...prev, actions: [] }));
      });
    }, []);

    const listData = useMemo(
      () => ({
        items: filteredDevices,
        deviceInfoById,
        selectedDeviceId,
        onDeviceSelect,
        onCopyLink: handleCopyLinkStable,
        statusLabels,
        statusColors: STATUS_COLOR_MAP,
      }),
      [filteredDevices, deviceInfoById, selectedDeviceId, onDeviceSelect, statusLabels]
    );

    const handleFilterOpen = useCallback(() => {
      setFilterEverOpened(true);
      setFilterAnchorEl(inputRef.current);
    }, []);

    const handleFilterClose = useCallback(
      () => setFilterAnchorEl(null),
      []
    );

    const handleStatusChange = useCallback(
      (e) =>
        startTransition(() =>
          setFilter((f) => ({ ...f, statuses: e.target.value }))
        ),
      []
    );

    const handleGroupsChange = useCallback(
      (e) =>
        startTransition(() =>
          setFilter((f) => ({ ...f, groups: e.target.value }))
        ),
      []
    );

    const handleSortChange = useCallback(
      (e) => startTransition(() => setFilterSort(e.target.value)),
      []
    );

    const removeStatusChip = useCallback(
      (s) =>
        startTransition(() =>
          setFilter((f) => ({
            ...f,
            statuses: f.statuses.filter((x) => x !== s),
          }))
        ),
      []
    );

    const removeGroupChip = useCallback(
      (gId) =>
        startTransition(() =>
          setFilter((f) => ({
            ...f,
            groups: f.groups.filter((x) => x !== gId),
          }))
        ),
      []
    );

    const removeActionChip = useCallback(
      (actionKey) =>
        startTransition(() =>
          setFilter((f) => ({
            ...f,
            actions: f.actions.filter((x) => x !== actionKey),
          }))
        ),
      []
    );

    const handleClearFilters = useCallback(() => {
      startTransition(() => {
        setFilter({ statuses: [], groups: [], actions: [] });
        setFilterSort('');
      });
      setKeyword('');
    }, []);

    return (
      <Paper elevation={0} sx={PAPER_SX}>
        <Box sx={HEADER_SX}>
          <IconButton size="small" sx={MAP_ICON_BTN_SX}>
            <MapIcon fontSize="small" />
          </IconButton>
          <OutlinedInput
            ref={inputRef}
            placeholder="Buscar Dispositivos"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            size="small"
            fullWidth
            sx={SEARCH_INPUT_SX}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  edge="end"
                  onClick={handleFilterOpen}
                  sx={{ mr: -0.5 }}
                >
                  <Badge
                    color="primary"
                    variant="dot"
                    invisible={!hasActiveFilters}
                  >
                    <TuneIcon fontSize="small" />
                  </Badge>
                </IconButton>
              </InputAdornment>
            }
          />
        </Box>
        <Box sx={FILTER_BUTTONS_SX}>
          {ACTION_FILTERS.map((action) => {
            const IconComponent = action.icon;
            const isActive = filter.actions.includes(action.key);
            return (
              <Tooltip key={action.key} title={`Filtrar por ${action.label}`}>
                <IconButton
                  size="small"
                  onClick={() => handleActionFilterToggle(action.key)}
                  sx={{
                    ...ACTION_FILTER_BTN_SX,
                    ...(isActive ? ACTION_FILTER_ACTIVE_SX : ACTION_FILTER_INACTIVE_SX),
                  }}
                >
                  <IconComponent sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            );
          })}
          {filter.actions.length > 0 && (
            <Tooltip title="Limpiar filtros de acción">
              <IconButton
                size="small"
                onClick={handleClearActionFilters}
                sx={{
                  ...ACTION_FILTER_BTN_SX,
                  ...ACTION_FILTER_INACTIVE_SX,
                  ml: 0.5,
                }}
              >
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {hasActiveFilters && (
          <Box sx={CHIPS_BAR_SX}>
            {filter.statuses.map((s) => (
              <Chip
                key={s}
                label={formatStatus(s)}
                size="small"
                onDelete={() => removeStatusChip(s)}
                sx={CHIP_SX}
              />
            ))}
            {filter.groups.map((gId) => (
              <Chip
                key={gId}
                label={groups[gId]?.name || `Grupo ${gId}`}
                size="small"
                onDelete={() => removeGroupChip(gId)}
                sx={CHIP_SX}
              />
            ))}
            {filter.actions.map((actionKey) => {
              const action = ACTION_FILTERS.find(a => a.key === actionKey);
              const IconComponent = action?.icon;
              return (
                <Chip
                  key={actionKey}
                  label={action?.label || actionKey}
                  size="small"
                  onDelete={() => removeActionChip(actionKey)}
                  icon={IconComponent ? <IconComponent sx={{ fontSize: 12 }} /> : undefined}
                  sx={CHIP_SX}
                />
              );
            })}
            <Chip
              label="Limpiar todo"
              size="small"
              onClick={handleClearFilters}
              icon={<ClearIcon />}
              sx={CLEAR_CHIP_SX}
            />
          </Box>
        )}
        {filterEverOpened && (
          <Popover
            open={!!filterAnchorEl}
            anchorEl={filterAnchorEl}
            onClose={handleFilterClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            PaperProps={POPOVER_PAPER_SX}
          >
            <Box sx={POPOVER_BOX_SX}>
              <FormControl size="small" fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={filter.statuses}
                  multiple
                  onChange={handleStatusChange}
                >
                  <MenuItem value="online">
                    En línea ({statusCounts.online})
                  </MenuItem>
                  <MenuItem value="offline">
                    Fuera de línea ({statusCounts.offline})
                  </MenuItem>
                  <MenuItem value="unknown">
                    Desconocido ({statusCounts.unknown})
                  </MenuItem>
                </Select>
              </FormControl>
              {Object.keys(groups).length > 0 && (
                <FormControl size="small" fullWidth>
                  <InputLabel>Grupos</InputLabel>
                  <Select
                    label="Grupos"
                    value={filter.groups}
                    multiple
                    onChange={handleGroupsChange}
                  >
                    {sortedGroups.map((g) => (
                      <MenuItem key={g.id} value={g.id}>
                        {g.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl size="small" fullWidth>
                <InputLabel>Ordenar</InputLabel>
                <Select
                  label="Ordenar"
                  value={filterSort}
                  onChange={handleSortChange}
                >
                  <MenuItem value="">Sin orden</MenuItem>
                  <MenuItem value="name">Nombre</MenuItem>
                  <MenuItem value="lastUpdate">
                    Última actualización
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Popover>
        )}
        <Box sx={LIST_AREA_SX}>
          {loading ? (
            <Box sx={LOADING_BOX_SX}>
              <CircularProgress size={32} />
            </Box>
          ) : filteredDevices.length === 0 ? (
            <Box sx={EMPTY_BOX_SX}>
              <DirectionsCarIcon sx={EMPTY_CAR_ICON_SX} />
              <Typography variant="body2" color="text.secondary">
                {keyword || hasActiveFilters
                  ? 'No se encontraron dispositivos'
                  : 'No hay dispositivos'}
              </Typography>
              {hasActiveFilters && (
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  sx={{ mt: 1 }}
                >
                  Limpiar filtros
                </Button>
              )}
            </Box>
          ) : (
            <AutoSizer style={{ minHeight: '600px' }}>
              {({ height, width }) => (
                <List
                  listRef={listRef}
                  rowComponent={SidebarDeviceRow}
                  rowCount={filteredDevices.length}
                  rowHeight={ROW_HEIGHT}
                  rowProps={listData}
                  overscanCount={OVERSCAN_COUNT}
                  defaultHeight={600}
                  defaultWidth={320}
                  style={{ height, width }}
                />
              )}
            </AutoSizer>
          )}
        </Box>
        <Box sx={FOOTER_SX}>
          <Button fullWidth sx={FOOTER_BTN_ACTIVE_SX}>
            <MapIcon sx={MAP_ICON_SX} />
            Mapa
          </Button>
          <Button fullWidth onClick={onReportsClick} sx={FOOTER_BTN_SX}>
            <AssessmentIcon sx={ASSESS_ICON_SX} />
            Reportes
          </Button>
          <Button fullWidth onClick={onSettingsClick} sx={FOOTER_BTN_SX}>
            <SettingsIcon sx={SETTINGS_ICON_SX} />
            Configuración
          </Button>
        </Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={2000}
          onClose={() => setSnackbarOpen(false)}
          message="Enlace copiado al portapapeles"
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </Paper>
    );
  },
  (prev, next) =>
    prev.devices === next.devices &&
    prev.positions === next.positions &&
    prev.groups === next.groups &&
    prev.selectedDeviceId === next.selectedDeviceId &&
    prev.loading === next.loading
);

TraccarSidebar.displayName = 'TraccarSidebar';

export default TraccarSidebar;
